/**
 * Extension state for feature gating — is a first-party extension switched on
 * for this deployment?
 *
 * Shares the `['extensions']` query key with the Extensions page and the case
 * settings panel, so all three read one cached list.
 */

import { useQuery } from '@tanstack/react-query'
import { extensionsApi } from '@/services/api/extensions'

/** The one cache entry for the extensions list — every consumer (this hook,
 * the Extensions page, case settings) must query and invalidate this key. */
export const EXTENSIONS_QUERY_KEY = ['extensions'] as const

/** Registry id of the OSINT agent extension (mirrors the backend registry). */
export const OSINT_EXTENSION_ID = 'osint'

/** Registry id of the Mobile Forensics extension (mirrors the backend registry). */
export const MOBILE_FORENSICS_EXTENSION_ID = 'mobile_forensics'

/** Registry id of the HUMINT workspace extension (mirrors the backend registry). */
export const HUMINT_EXTENSION_ID = 'humint'

/** Registry id of the Advanced Face Detection extension (mirrors the backend registry).
 * Face detection, matching and merge proposals; HUMINT requires it (ADR-0051). */
export const ADVANCED_FACE_DETECTION_EXTENSION_ID = 'advanced_face_detection'

/** Registry id of the Video Processing extension (mirrors the backend registry): video uploads,
 * analysis and every video read (PRD-0074). It requires Advanced Face Detection. */
export const VIDEO_PROCESSING_EXTENSION_ID = 'video_processing'

export interface ExtensionGate {
  /** False until the list arrives — gated UI stays hidden rather than flashing. */
  enabled: boolean
  isLoading: boolean
  /** Required extensions that are off and keep this available one off too (ADR-0051), so a
   * wall can say what to switch on first. Empty when it is enabled or not available here. */
  missingRequirements: readonly string[]
}

const NO_REQUIREMENTS: readonly string[] = []

/** Ids of every enabled extension — one cached read for surfaces (nav,
 * route guards) that gate on more than one extension. Empty while loading,
 * so gated UI stays hidden rather than flashing. */
export function useEnabledExtensionIds(): ReadonlySet<string> {
  const { data } = useQuery({
    queryKey: EXTENSIONS_QUERY_KEY,
    queryFn: () => extensionsApi.list(),
    staleTime: 60_000,
  })
  return new Set(data?.filter(e => e.enabled).map(e => e.id))
}

export function useExtensionEnabled(id: string): ExtensionGate {
  const { data, isLoading } = useQuery({
    queryKey: EXTENSIONS_QUERY_KEY,
    queryFn: () => extensionsApi.list(),
    staleTime: 60_000,
  })
  const extension = data?.find(e => e.id === id)
  return {
    enabled: extension?.enabled ?? false,
    isLoading,
    missingRequirements:
      extension?.available && !extension.enabled
        ? (extension.missing_requirements ?? NO_REQUIREMENTS)
        : NO_REQUIREMENTS,
  }
}
